// =============================================================================
// Central Operacional — a fila do que exige decisão
//
//   "Apresentar o que exige ação, com prioridade, contexto, responsável, prazo
//    e acesso direto à tratativa." (PRD §5)
//
//   "Divergências devem gerar fila estruturada, não duplicação de registros ou
//    controles paralelos." (PRD §5)
//
// A tela é ordenada por severidade e prazo, não por data de criação: o que
// importa é o que precisa de decisão hoje, não o que chegou primeiro.
//
// Pedido do Financeiro de 20/10:
//   4.1 — selecionar várias e resolver (ou confirmar sugestões) de uma vez;
//   4.3 — a fila vai ser do sistema inteiro, então filtra por MÓDULO primeiro
//         (orçamento, contratos, integração) e por tipo dentro dele.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, FalhaApi } from "../lib/api.ts";
import { dataCurta, type Tom } from "../lib/formato.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { Modal } from "../componentes/Modal.tsx";
import { ModalAlocacao } from "./ModalAlocacao.tsx";
import { ModalRenovacao, type AlvoRenovacao } from "./ModalRenovacao.tsx";

/** Migration 064: a que parte do sistema a pendência pertence. A regra mora no
 *  banco (`fn_modulo_excecao`); aqui só se dá nome. */
type Modulo = "orcamento" | "contratos" | "integracao" | "outros";

type Excecao = {
  excecao_id: string;
  /** Migration 041: as pendências que este item da fila representa. O estouro
   *  da mesma linha em meses diferentes é um item só. */
  excecao_ids: string[];
  quantidade: number;
  tipo: string;
  /** Migration 064. Opcional porque a API anterior não o manda — e a tela não
   *  pode quebrar no intervalo entre o deploy do front e o da API. */
  modulo?: Modulo;
  severidade: "alta" | "media" | "baixa";
  entidade: string | null;
  entidade_id: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  prazo: string | null;
  estado: "aberta" | "em_tratativa" | "resolvida";
  criado_em: string;
  dias_aberta: number;
  contexto: Record<string, unknown> | null;
};

type ContagemTipo = { tipo: string; modulo?: string; n: number };

/**
 * Cada tipo ganha um rótulo em português e — o que importa — uma frase dizendo
 * **o que fazer**. Uma fila que diz "baixa_confianca" e nada mais transfere
 * para o Financeiro o trabalho de lembrar o que aquilo significa, todo mês.
 */
const TIPOS: Record<string, { rotulo: string; oQueFazer: string }> = {
  sem_correspondencia: {
    rotulo: "Sem centro de custo",
    oQueFazer:
      "Esta parte do lançamento chegou sem um centro de custo utilizável. Se o lançamento tem várias categorias, confira o rateio de centro de custo de cada categoria no Conta Azul: o centro exibido no cabeçalho pode não vir na parte que gerou a pendência. Corrija o rateio na origem e reprocese a competência; a pendência sai quando essa parte passar a ter centro de custo. Se não houver linha orçada, ela vira “não orçado”.",
  },
  baixa_confianca: {
    rotulo: "Sugestão a confirmar",
    oQueFazer:
      "O motor achou uma linha provável pela descrição, mas não confirma sozinho. Em \"Confirmar ou trocar de linha\", confirme a sugestão ou escolha a linha certa — a pendência sai da fila no mesmo passo.",
  },
  possivel_duplicidade: {
    rotulo: "Possível duplicidade",
    oQueFazer:
      "Mesmo fornecedor, mesmo valor e mesma data — sinal de cobrança duplicada. Se forem lançamentos legítimos distintos, resolva com a observação explicando.",
  },
  nao_orcado: {
    rotulo: "Não orçado",
    oQueFazer:
      "Gasto sem linha orçada no mesmo centro de custo, categoria e mês. Se for recorrente, cadastre a linha — quando ela existir, a pendência sai sozinha na reconciliação seguinte. Se foi um gasto fora do plano, resolva com uma observação.",
  },
  estouro: {
    rotulo: "Estouro do teto",
    oQueFazer:
      "O realizado passou do orçado. Os meses da mesma linha aparecem juntos: estourar mês após mês costuma ser orçado subdimensionado, e a decisão é uma só. O gestor justifica no painel dele: se for uso pontual, o mês sai sozinho. Se for upgrade, renegociação ou outro motivo, fica aqui como aviso — ajuste os lançamentos futuros ou avalie, e resolva. Se o orçado for revisto ou o gasto corrigido, também sai sozinho.",
  },
  falha_integracao: {
    rotulo: "Falha de integração",
    oQueFazer:
      "A sincronização com a origem falhou. O reprocessamento é controlado: confira a linhagem do dado antes de dar por resolvida.",
  },
  natureza_divergente: {
    rotulo: "Natureza divergente",
    oQueFazer:
      "A natureza que a origem informou difere da natureza da nossa categoria. A nossa vence na agregação; aqui se decide se a classificação precisa mudar.",
  },
  ausente_na_origem: {
    rotulo: "Sumiu da origem",
    oQueFazer:
      "O lançamento deixou de existir no sistema de origem e saiu do realizado a partir daí. Confirme se foi exclusão intencional.",
  },
  competencia_na_descricao: {
    rotulo: "Competência na descrição",
    oQueFazer:
      "A descrição traz uma marcação de competência que o sistema não pôde usar: o mês é inválido, falta o ano, ou fica a mais de 12 meses do mês em que o lançamento está no Conta Azul. Por isso ele continua no mês do Conta Azul. Corrija a descrição lá, no formato “competência 10/2026”: na sincronização seguinte o lançamento vai para o mês certo e a pendência sai sozinha. Se o mês do Conta Azul estiver certo, resolva com uma observação.",
  },
  renovacao: {
    rotulo: "Renovação de contrato",
    oQueFazer:
      "Contrato com renovação manual chegando: o sistema avisa aos 60 dias, de novo aos 30 e a 2 dias da data, e o prazo é a data da renovação. Decida se renova, renegocia ou cancela antes dela. Se renovar, clique em “Informar renovação” e coloque a data do ciclo seguinte: a pendência sai sozinha. Se cancelar, tire a renovação manual no mesmo lugar, ou resolva com uma observação. As datas dos contratos se informam na Planilha orçamentária: na aba Lançar (Contrato já orçado) ou na própria planilha, ao lado de cada linha e subcategoria.",
  },
  categoria_desconhecida: {
    rotulo: "Categoria nova no Conta Azul",
    oQueFazer:
      "O Conta Azul usou uma categoria que ainda não existe aqui — quase sempre criada lá depois da última carga do plano de contas. Enquanto isso, o lançamento fica fora do Realizado da categoria. Peça a carga do plano de contas; na sincronização seguinte ele é classificado.",
  },
};

/** Na ordem em que aparecem nos filtros. "Outros" é o tipo que o banco ainda
 *  não pôs em módulo nenhum — e o teste da 064 acusa quando isso acontece. */
const MODULOS: { id: Modulo; rotulo: string }[] = [
  { id: "orcamento", rotulo: "Orçamento" },
  { id: "contratos", rotulo: "Contratos e assinaturas" },
  { id: "integracao", rotulo: "Integração" },
  { id: "outros", rotulo: "Outros" },
];
const ROTULO_MODULO: Record<Modulo, string> = Object.fromEntries(
  MODULOS.map((m) => [m.id, m.rotulo]),
) as Record<Modulo, string>;

/** Valor desconhecido — ou ausente, na API anterior à 064 — conta como "outros":
 *  a pendência continua aparecendo, só sem módulo. */
function moduloDe(m: unknown): Modulo {
  return MODULOS.some((x) => x.id === m) ? (m as Modulo) : "outros";
}

/**
 * Tipos que saem da fila sozinhos quando a causa é corrigida. Resolver à mão
 * tira da fila DE VEZ — pendência resolvida por pessoa não volta (18/09), mesmo
 * que a causa continue. Num lote, isso merece aviso antes do clique.
 */
const SAEM_SOZINHAS = new Set([
  "sem_correspondencia", "nao_orcado", "estouro", "competencia_na_descricao",
  "categoria_desconhecida", "renovacao",
]);

const POR_PAGINA = 200;
/** Limite da rota `/api/alocacoes/confirmar-em-lote`. Acima disso, lotes em sequência. */
const LIMITE_CONFIRMAR = 500;

const TOM_SEVERIDADE: Record<string, Tom> = { alta: "risco", media: "atencao", baixa: "neutro" };
const ROTULO_SEVERIDADE: Record<string, string> = { alta: "Alta", media: "Média", baixa: "Baixa" };

/** Só a sugestão de lançamento tem o que confirmar: é a alocação sugerida dele. */
const ehSugestaoDeLancamento = (e: Excecao) =>
  e.tipo === "baixa_confianca" && e.entidade === "lancamento_realizado" && !!e.entidade_id;

/** "1 pendência" / "3 pendências", com o número em pt-BR. */
function contar(n: number, um: string, varios: string): string {
  return `${n.toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;
}

export function CentralOperacional() {
  const qc = useQueryClient();
  const [modulo, setModulo] = useState<Modulo | "todos">("todos");
  const [filtro, setFiltro] = useState<string>("todas");
  // Os modais em lote recebem uma FOTO dos itens no clique: a lista pode se
  // refazer enquanto o modal está aberto, e o que se decide é o que se viu.
  // `lote` diz de onde veio: da barra de seleção (mesmo com uma só marcada) ou
  // do "Tratar" do cartão — o que muda o que acontece com a seleção depois.
  const [tratando, setTratando] = useState<{ itens: Excecao[]; lote: boolean } | null>(null);
  const [confirmando, setConfirmando] = useState<Excecao[] | null>(null);
  const [decidindo, setDecidindo] = useState<string | null>(null);
  const [renovando, setRenovando] = useState<AlvoRenovacao | null>(null);
  const [selecao, setSelecao] = useState<ReadonlySet<string>>(() => new Set());
  const [aviso, setAviso] = useState<{ texto: string; rolar: boolean } | null>(null);
  const refAviso = useRef<HTMLDivElement>(null);

  // O filtro por tipo e a contagem vêm do SERVIDOR. Até 18/09 a tela lia as
  // 200 primeiras por severidade e filtrava aqui: com a fila de produção, as
  // sugestões a confirmar (severidade baixa) nunca entravam nas 200, e os
  // contadores dos filtros contavam só o que tinha sido carregado.
  const q = useInfiniteQuery({
    queryKey: ["excecoes", modulo, filtro],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(pageParam) });
      if (modulo !== "todos") p.set("modulo", modulo);
      if (filtro !== "todas") p.set("tipo", filtro);
      return api.get<{ excecoes: Excecao[]; por_tipo: ContagemTipo[] }>(`/api/excecoes?${p}`);
    },
    initialPageParam: 0,
    getNextPageParam: (ultima, todas) => (ultima.excecoes.length < POR_PAGINA ? undefined : todas.length * POR_PAGINA),
  });

  const excecoes = q.data?.pages.flatMap((p) => p.excecoes) ?? [];

  // `por_tipo` é a fila INTEIRA, independente do filtro: é dele que saem os
  // totais dos dois níveis. O do módulo é a soma dos tipos dele.
  const porTipo = (q.data?.pages[0]?.por_tipo ?? []).map((x) => ({ tipo: x.tipo, modulo: moduloDe(x.modulo), n: x.n }));
  const totalFila = porTipo.reduce((s, x) => s + x.n, 0);
  const porModulo = MODULOS.map((m) => ({
    ...m,
    n: porTipo.filter((x) => x.modulo === m.id).reduce((s, x) => s + x.n, 0),
  }));
  const tiposDoModulo = porTipo.filter((x) => modulo === "todos" || x.modulo === modulo);
  const totalDoModulo = tiposDoModulo.reduce((s, x) => s + x.n, 0);
  const totalDoFiltro = filtro === "todas" ? totalDoModulo : (tiposDoModulo.find((x) => x.tipo === filtro)?.n ?? 0);

  // ⚠️ O filtro escolhido continua à vista mesmo quando zera — o caso comum
  // depois de resolver todas de um tipo em lote. Sem isto o chip some, nenhum
  // fica marcado, e a lista vazia parece defeito.
  const chipsModulo = porModulo.filter((m) => m.n > 0 || m.id === modulo);
  const chipsTipo =
    filtro !== "todas" && !tiposDoModulo.some((x) => x.tipo === filtro)
      ? [...tiposDoModulo, { tipo: filtro, modulo: moduloDe(modulo), n: 0 }]
      : tiposDoModulo;

  const atrasadas = excecoes.filter(
    (e) => e.prazo && e.prazo < hojeISO() && e.contexto?.justificativa_do_gestor == null,
  ).length;

  // ── seleção ─────────────────────────────────────────────────────────────
  // Por item da fila, e derivada da lista em tela: o que saiu da fila (resolvido
  // por outra pessoa, ou pela sincronização) deixa de contar sozinho.
  const selecionadas = excecoes.filter((e) => selecao.has(e.excecao_id));
  const pendenciasSelecionadas = selecionadas.reduce((s, e) => s + (e.quantidade ?? 1), 0);
  const soSugestoes = selecionadas.length > 0 && selecionadas.every(ehSugestaoDeLancamento);
  const todasCarregadasMarcadas = excecoes.length > 0 && selecionadas.length === excecoes.length;

  const limparSelecao = () => setSelecao(new Set());
  const alternar = (id: string) =>
    setSelecao((antes) => {
      const s = new Set(antes);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  // Trocar de filtro limpa a seleção: marcar no filtro A e agir no filtro B
  // agiria sobre pendências que a pessoa não está vendo.
  const trocarModulo = (m: Modulo | "todos") => {
    setModulo(m);
    setFiltro("todas");
    limparSelecao();
    setAviso(null);
  };
  const trocarTipo = (t: string) => {
    setFiltro(t);
    limparSelecao();
    setAviso(null);
  };

  // Estável: o `Modal` refaz o efeito (e devolve o foco) quando `onFechar` muda.
  const fecharModais = useCallback(() => {
    setTratando(null);
    setConfirmando(null);
  }, []);

  const atualizar = (tudo: boolean) => {
    // Confirmar mexe em alocação, e com ela no realizado por linha de toda tela
    // — é o que o `ModalAlocacao` já faz por um lançamento. Tratar pendência só
    // mexe na fila e no que a conta.
    if (tudo) void qc.invalidateQueries();
    else
      void qc.invalidateQueries({
        predicate: (x) => ["excecoes", "visao-financeiro", "contadores", "avisos"].includes(String(x.queryKey[0])),
      });
  };

  /** Depois de qualquer decisão: refaz o que ela mexeu e diz o que aconteceu. */
  const concluir = (texto: string, { lote, tudo = false }: { lote: boolean; tudo?: boolean }) => {
    fecharModais();
    // Um "Tratar" avulso não desfaz a seleção que a pessoa estava montando.
    if (lote) limparSelecao();
    setAviso({ texto, rolar: lote });
    atualizar(tudo);
  };

  // A barra de ação fica no rodapé e o aviso no topo da lista: depois de um
  // lote, quem clicou está lá embaixo e não veria o resultado.
  useEffect(() => {
    if (aviso?.rolar) refAviso.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [aviso]);

  const idsSugestoes = soSugestoes ? [...new Set(selecionadas.map((e) => e.entidade_id!))] : [];

  return (
    // Com a barra de ação aberta, a página ganha respiro embaixo para ela não
    // cobrir o último cartão nem o "Carregar mais".
    <div className={`pagina ${selecionadas.length > 0 ? "pb-36" : "pb-20"}`}>
      <header className="py-7">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Operação
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Central Operacional
        </h1>
        <p className="mt-0.5 max-w-2xl text-[13.5px] text-tinta-media">
          O que o motor não resolveu sozinho e precisa de decisão. Ordenado por severidade e prazo
          — não por ordem de chegada.
        </p>
      </header>

      {aviso && (
        <div
          ref={refAviso}
          role="status"
          className="mb-4 flex scroll-mt-4 items-start justify-between gap-3 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta"
        >
          <span>{aviso.texto}</span>
          <button
            type="button"
            onClick={() => setAviso(null)}
            aria-label="Fechar aviso"
            className="shrink-0 text-[12px] font-semibold opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      )}

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}

      {q.isError && (
        <Vazio
          titulo="Não foi possível carregar a fila"
          dica={(q.error as { message?: string })?.message}
        />
      )}

      {q.data && totalFila === 0 && (
        <Painel>
          <Vazio
            titulo="Nenhuma pendência aberta"
            dica="Tudo o que veio da origem casou com uma linha orçamentária, ou já foi tratado. Quando algo não casar, aparece aqui."
          />
        </Painel>
      )}

      {q.data && totalFila > 0 && (
        <>
          {/* Primeiro nível: de que parte do sistema é a pendência. */}
          <div role="group" aria-label="Filtrar por módulo" className="mb-2 flex flex-wrap items-center gap-2">
            <Chip ativo={modulo === "todos"} onClick={() => trocarModulo("todos")}>
              Todos os módulos · {totalFila.toLocaleString("pt-BR")}
            </Chip>
            {chipsModulo.map((m) => (
              <Chip key={m.id} ativo={modulo === m.id} onClick={() => trocarModulo(m.id)}>
                {m.rotulo} · {m.n.toLocaleString("pt-BR")}
              </Chip>
            ))}
          </div>

          {/* Segundo nível: o tipo, só os do módulo escolhido. */}
          <div role="group" aria-label="Filtrar por tipo" className="mb-4 flex flex-wrap items-center gap-2">
            <Chip nivel="tipo" ativo={filtro === "todas"} onClick={() => trocarTipo("todas")}>
              Todos os tipos · {totalDoModulo.toLocaleString("pt-BR")}
            </Chip>
            {chipsTipo.map((x) => (
              <Chip key={x.tipo} nivel="tipo" ativo={filtro === x.tipo} onClick={() => trocarTipo(x.tipo)}>
                {TIPOS[x.tipo]?.rotulo ?? x.tipo} · {x.n.toLocaleString("pt-BR")}
              </Chip>
            ))}
            {atrasadas > 0 && (
              <span className="ml-auto text-[12.5px] font-semibold text-risco-tinta">
                {atrasadas} fora do prazo{excecoes.length < totalDoFiltro ? " entre as carregadas" : ""}
              </span>
            )}
          </div>

          {excecoes.length === 0 ? (
            <Painel>
              <Vazio
                titulo="Nenhuma pendência neste filtro"
                dica="As deste módulo ou tipo já saíram da fila. Escolha outro filtro acima para ver o que ainda está aberto."
              />
            </Painel>
          ) : (
            <>
              {/* Selecionar alcança só o que está carregado: marcar o que a
                  pessoa não viu seria decidir às cegas. */}
              <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]">
                <button
                  type="button"
                  disabled={todasCarregadasMarcadas}
                  onClick={() => setSelecao(new Set(excecoes.map((e) => e.excecao_id)))}
                  className="font-semibold text-tinta-media hover:text-azul disabled:cursor-default disabled:opacity-45 disabled:hover:text-tinta-media"
                >
                  Selecionar todas as carregadas ({excecoes.length.toLocaleString("pt-BR")})
                </button>
                {selecionadas.length > 0 && (
                  <button type="button" onClick={limparSelecao} className="font-semibold text-tinta-media hover:text-azul">
                    Limpar seleção
                  </button>
                )}
                {excecoes.length < totalDoFiltro && (
                  <span className="text-tinta-fraca">
                    Só entram as que já estão na tela. Para incluir mais, use “Carregar mais” no fim da lista.
                  </span>
                )}
              </div>

              <ul className="grid gap-2">
                {excecoes.map((e) => {
                  const t = TIPOS[e.tipo];
                  // O prazo do estouro é o do gestor justificar (063): justificado, foi cumprido.
                  const atrasada = !!e.prazo && e.prazo < hojeISO() && e.contexto?.justificativa_do_gestor == null;
                  const marcada = selecao.has(e.excecao_id);
                  const qual = e.contexto?.lancamento ?? e.contexto?.linha;
                  return (
                    <li
                      key={e.excecao_id}
                      className={`rounded-lg border p-4 ${
                        marcada
                          ? "border-laranja bg-laranja/5"
                          : atrasada
                            ? "border-risco-linha bg-superficie"
                            : "border-linha bg-superficie"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={marcada}
                          onChange={() => alternar(e.excecao_id)}
                          aria-label={`Selecionar: ${t?.rotulo ?? e.tipo}${qual != null ? ` — ${String(qual)}` : ""}`}
                          className="mt-1 size-4 shrink-0 cursor-pointer accent-laranja"
                        />
                        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-3">
                          <div className="min-w-[260px] flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-[14.5px] font-bold">{t?.rotulo ?? e.tipo}</h2>
                              <Pilula tom={TOM_SEVERIDADE[e.severidade] ?? "neutro"}>
                                {ROTULO_SEVERIDADE[e.severidade] ?? e.severidade}
                              </Pilula>
                              {/* O módulo só diz algo quando a lista mistura módulos.
                                  Sem o campo (API anterior à 064), não se inventa um. */}
                              {modulo === "todos" && e.modulo && (
                                <span className="rounded-full border border-linha px-2 py-0.5 text-[11px] font-medium whitespace-nowrap text-tinta-fraca">
                                  {ROTULO_MODULO[moduloDe(e.modulo)]}
                                </span>
                              )}
                              {e.estado === "em_tratativa" && <Pilula tom="atencao">Em tratativa</Pilula>}
                            </div>

                            {/* Qual lançamento é (migration 036). Sem isto, a fila
                                era uma pilha de cartões iguais. */}
                            {qual != null && (
                              <p className="mt-1 text-[13px] font-semibold text-tinta">
                                {String(qual)}
                                <span className="font-normal text-tinta-media">
                                  {e.quantidade > 1 && <> · <strong className="font-semibold text-tinta">{e.quantidade} meses</strong></>}
                                  {[e.contexto?.fornecedor, e.contexto?.competencia].filter((x) => x != null).map((x) => ` · ${String(x)}`).join("")}
                                </span>
                              </p>
                            )}
                            {/* Migration 037: o que o gestor justificou. A pendência de
                                estouro que fica depois da justificativa é o aviso que a
                                tela dele prometeu ao Financeiro. */}
                            {e.contexto?.justificativa_do_gestor != null && (
                              <p className="mt-1 rounded-lg bg-atencao-bg px-2 py-1 text-[12.5px] text-atencao-tinta">
                                Gestor justificou{e.quantidade > 1 ? " (por mês)" : ""}: {String(e.contexto.justificativa_do_gestor)}
                              </p>
                            )}

                            {/* O que fazer vem antes do metadado: quem abre a fila
                                precisa da ação, não do identificador. */}
                            <p className="mt-1.5 max-w-2xl text-[13px] text-tinta-media">
                              {t?.oQueFazer ?? "Sem orientação cadastrada para este tipo."}
                            </p>

                            <p className="num mt-2 text-[12px] text-tinta-fraca">
                              {e.responsavel_nome && <>responsável {e.responsavel_nome} · </>}
                              aberta há {e.dias_aberta} {e.dias_aberta === 1 ? "dia" : "dias"}
                              {e.prazo && (
                                <span className={atrasada ? "font-semibold text-risco-tinta" : ""}>
                                  {" · "}prazo {dataCurta(e.prazo)}
                                  {atrasada && " (vencido)"}
                                </span>
                              )}
                              {e.entidade && <> · {e.entidade}</>}
                            </p>
                          </div>

                          <div className="flex shrink-0 flex-wrap gap-2">
                            {/* 050: renovar é informar a data do ciclo seguinte — o aviso sai sozinho. */}
                            {e.tipo === "renovacao" && e.contexto?.renovacao != null && (
                              <button
                                type="button"
                                onClick={() => {
                                  const r = e.contexto!.renovacao as Record<string, string | undefined>;
                                  const registrado = e.entidade === "renovacao_contrato";
                                  setRenovando({
                                    centro_custo_id: r.centro_custo_id!, categoria_id: r.categoria_id!,
                                    subcategoria: r.subcategoria ?? null, rotulo: r.rotulo ?? "",
                                    proxima_renovacao: registrado ? (r.proxima_renovacao ?? null) : null,
                                    observacao: r.observacao ?? null, registrado,
                                  });
                                }}
                                className="rounded-lg bg-laranja px-3 py-1.5 text-[12.5px] font-bold whitespace-nowrap text-azul"
                              >
                                Informar renovação
                              </button>
                            )}
                            {ehSugestaoDeLancamento(e) && (
                              <button
                                type="button"
                                onClick={() => setDecidindo(e.entidade_id)}
                                className="rounded-lg bg-laranja px-3 py-1.5 text-[12.5px] font-bold whitespace-nowrap text-azul"
                              >
                                Confirmar ou trocar de linha
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setTratando({ itens: [e], lote: false })}
                              className="rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap hover:border-laranja hover:text-laranja-tinta"
                            >
                              Tratar
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <div className="mt-4 flex flex-col items-center gap-2 text-[12.5px] text-tinta-fraca">
            <span className="num">
              mostrando {excecoes.length.toLocaleString("pt-BR")} de {totalDoFiltro.toLocaleString("pt-BR")}
            </span>
            {q.hasNextPage && (
              <button type="button" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}
                className="rounded-lg border border-linha bg-superficie px-4 py-2 text-[13px] font-semibold text-tinta-media hover:bg-superficie-2 disabled:opacity-45">
                {q.isFetchingNextPage ? "Carregando…" : "Carregar mais"}
              </button>
            )}
          </div>
        </>
      )}

      {/* ── barra de ação do lote (pedido 4.1) ─────────────────────────────── */}
      {selecionadas.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-linha bg-superficie shadow-[0_-4px_12px_rgba(16,40,80,0.08)]">
          <div className="pagina flex flex-wrap items-center gap-3 py-3">
            <span className="text-[13px] font-semibold">
              {contar(selecionadas.length, "selecionada", "selecionadas")}
              {pendenciasSelecionadas !== selecionadas.length && (
                <span className="font-normal text-tinta-media"> · {contar(pendenciasSelecionadas, "pendência", "pendências")}</span>
              )}
            </span>
            <button type="button" onClick={limparSelecao} className="text-[12.5px] font-semibold text-tinta-media hover:text-azul">
              Limpar seleção
            </button>
            <span className="ml-auto flex flex-wrap gap-2">
              {/* Só quando TODAS são sugestão de lançamento: confirmar não tem
                  sentido para as outras, e confirmar metade calado confundiria. */}
              {soSugestoes && (
                <button
                  type="button"
                  onClick={() => setConfirmando(selecionadas)}
                  className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul"
                >
                  Confirmar sugestões ({idsSugestoes.length.toLocaleString("pt-BR")})
                </button>
              )}
              <button
                type="button"
                onClick={() => setTratando({ itens: selecionadas, lote: true })}
                className={
                  soSugestoes
                    ? "rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold hover:border-laranja hover:text-laranja-tinta"
                    : "rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul"
                }
              >
                Resolver selecionadas…
              </button>
            </span>
          </div>
        </div>
      )}

      {decidindo && <ModalAlocacao lancamentoId={decidindo} onFechar={() => setDecidindo(null)} />}
      {renovando && <ModalRenovacao alvo={renovando} onFechar={() => setRenovando(null)} />}

      {tratando && (
        <ModalTratativa
          itens={tratando.itens}
          onFechar={fecharModais}
          onTratado={(texto) => concluir(texto, { lote: tratando.lote })}
        />
      )}

      {confirmando && (
        <ModalConfirmarSugestoes
          itens={confirmando}
          onFechar={fecharModais}
          onConcluido={(texto) => concluir(texto, { lote: true, tudo: true })}
          onParcial={() => atualizar(true)}
        />
      )}
    </div>
  );
}

function hojeISO(): string {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;
}

/** Chip de filtro. O nível do módulo é o cheio; o do tipo, o mais leve — a
 *  hierarquia se lê pela cor antes de se ler o rótulo. */
function Chip({
  ativo,
  onClick,
  children,
  nivel = "modulo",
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
  nivel?: "modulo" | "tipo";
}) {
  const ativoClasse = nivel === "modulo" ? "border-azul bg-azul text-white" : "border-azul bg-azul/10 text-azul";
  return (
    <button
      type="button"
      aria-pressed={ativo}
      onClick={onClick}
      className={`rounded-lg border px-3 py-1 text-[12.5px] font-semibold ${
        ativo ? ativoClasse : "border-linha bg-superficie text-tinta-media hover:bg-superficie-2"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Assumir ou resolver — um item (o "Tratar" do cartão) ou vários (a barra de
 * seleção). É o mesmo ato e a mesma regra: resolver exige observação, porque é
 * ela que explica a decisão a quem consultar o Histórico depois.
 */
function ModalTratativa({
  itens,
  onFechar,
  onTratado,
}: {
  itens: Excecao[];
  onFechar: () => void;
  onTratado: (mensagem: string) => void;
}) {
  const [decisao, setDecisao] = useState<"em_tratativa" | "resolvida">("resolvida");
  const [observacao, setObservacao] = useState("");

  const um = itens.length === 1 ? itens[0]! : null;
  // Assumir em lote deixa de fora o que já está em tratativa: o banco aceitaria
  // (não é transição), mas gravaria um evento por pendência dizendo nada.
  const alvo = !um && decisao === "em_tratativa" ? itens.filter((e) => e.estado !== "em_tratativa") : itens;
  const jaEmTratativa = itens.length - alvo.length;
  const ids = alvo.flatMap((e) => (e.excecao_ids?.length ? e.excecao_ids : [e.excecao_id]));
  const pendencias = alvo.reduce((s, e) => s + (e.quantidade ?? 1), 0);
  const pendenciasTotais = itens.reduce((s, e) => s + (e.quantidade ?? 1), 0);

  const tratar = useMutation({
    mutationFn: async (): Promise<string> => {
      const obs = observacao.trim() || null;
      if (ids.length === 1) {
        await api.post(`/api/excecoes/${ids[0]}/tratar`, { decisao, observacao: obs });
        return decisao === "resolvida"
          ? "Pendência resolvida."
          : "Pendência marcada como em tratativa. Ela continua na fila.";
      }
      // Resolver é UM evento no Histórico para o lote inteiro (025); assumir é
      // pendência por pendência, porque cada uma passa a ter responsável.
      const r = await api.post<{ resolvidas?: number; ja_resolvidas_ou_inexistentes?: number; tratadas?: number }>(
        "/api/excecoes/tratar-em-lote",
        { ids, decisao, observacao: obs },
      );
      if (decisao === "resolvida") {
        const n = r?.resolvidas ?? ids.length;
        const ja = r?.ja_resolvidas_ou_inexistentes ?? 0;
        return (
          `${contar(n, "pendência resolvida", "pendências resolvidas")}, com um registro só no Histórico para o lote.` +
          (ja === 1 ? " 1 já tinha saído da fila e ficou como estava." : ja > 1 ? ` ${ja.toLocaleString("pt-BR")} já tinham saído da fila e ficaram como estavam.` : "")
        );
      }
      const n = r?.tratadas ?? ids.length;
      return (
        (n === 1
          ? "1 pendência marcada como em tratativa. Ela continua na fila, e passa a ter você como responsável se ainda não tinha um."
          : `${n.toLocaleString("pt-BR")} pendências marcadas como em tratativa. Continuam na fila; as que não tinham responsável passam a ter você.`) +
        (jaEmTratativa === 1 ? " 1 já estava em tratativa e ficou como estava." : jaEmTratativa > 1 ? ` ${jaEmTratativa.toLocaleString("pt-BR")} já estavam em tratativa e ficaram como estavam.` : "")
      );
    },
    onSuccess: onTratado,
  });

  // A RPC exige observação para resolver, e recusa com NF004. Melhor impedir
  // aqui, com a explicação do porquê, do que deixar o erro chegar de volta.
  const precisaObservacao = decisao === "resolvida";
  const podeEnviar = ids.length > 0 && (!precisaObservacao || observacao.trim().length >= 5) && !tratar.isPending;

  const t = um ? TIPOS[um.tipo] : undefined;

  // Resumo do lote por tipo: o que se vai decidir, antes de decidir.
  const resumo = um
    ? []
    : [...itens.reduce((m, e) => {
        const r = m.get(e.tipo) ?? { itens: 0, pendencias: 0 };
        r.itens += 1;
        r.pendencias += e.quantidade ?? 1;
        return m.set(e.tipo, r);
      }, new Map<string, { itens: number; pendencias: number }>())].sort((a, b) => b[1].pendencias - a[1].pendencias);
  const quaisSaemSozinhas = resumo.filter(([tipo]) => SAEM_SOZINHAS.has(tipo)).map(([tipo]) => TIPOS[tipo]?.rotulo ?? tipo);

  return (
    <Modal titulo={um ? (t?.rotulo ?? um.tipo) : "Tratar as selecionadas"} onFechar={onFechar}>
      {um ? (
        <>
          <p className="text-[13px] text-tinta-media">{t?.oQueFazer}</p>
          {(um.excecao_ids?.length ?? 1) > 1 && (
            <p className="mt-2 rounded border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
              Este item junta {um.quantidade} meses da mesma linha. A decisão vale para todos eles.
            </p>
          )}

          {um.contexto && Object.keys(um.contexto).length > 0 && (
            <dl className="mt-3 grid gap-1 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px]">
              {Object.entries(um.contexto)
                .filter(([, v]) => v !== null && v !== undefined)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <dt className="text-tinta-fraca">{k.replace(/_/g, " ")}</dt>
                    <dd className="num font-medium">{String(v)}</dd>
                  </div>
                ))}
            </dl>
          )}
        </>
      ) : (
        <>
          <p className="text-[13px] text-tinta-media">
            {contar(itens.length, "item da fila", "itens da fila")}
            {pendenciasTotais !== itens.length && <>, com {contar(pendenciasTotais, "pendência", "pendências")} ao todo</>}
            . A decisão e a observação valem para todas.
          </p>
          <ul className="mt-3 grid gap-1 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px]">
            {resumo.map(([tipo, r]) => (
              <li key={tipo} className="flex justify-between gap-4">
                <span className="text-tinta-media">{TIPOS[tipo]?.rotulo ?? tipo}</span>
                <span className="num font-medium">
                  {r.pendencias.toLocaleString("pt-BR")}
                  {r.pendencias !== r.itens && <span className="font-normal text-tinta-fraca"> em {contar(r.itens, "item", "itens")}</span>}
                </span>
              </li>
            ))}
          </ul>
          {decisao === "resolvida" && quaisSaemSozinhas.length > 0 && (
            <p className="mt-2 rounded border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
              {quaisSaemSozinhas.join(", ")}: {quaisSaemSozinhas.length === 1 ? "essa sai" : "essas saem"} da fila
              sozinha{quaisSaemSozinhas.length === 1 ? "" : "s"} quando a causa é corrigida. Resolver aqui tira da fila de
              vez — pendência resolvida por pessoa não volta, mesmo que a causa continue.
            </p>
          )}
        </>
      )}

      <fieldset className="mt-4">
        <legend className="text-[12px] font-semibold text-tinta-media">Decisão</legend>
        <div className="mt-1.5 grid gap-2">
          {(
            [
              ["em_tratativa", "Assumir", um
                ? "Marca que alguém está cuidando. A pendência continua na fila."
                : "Marca que alguém está cuidando de cada uma. Elas continuam na fila."],
              ["resolvida", "Resolver", um
                ? "Encerra a pendência. Exige uma observação dizendo o que foi feito."
                : "Encerra todas, com um registro só no Histórico para o lote. Exige uma observação dizendo o que foi feito."],
            ] as const
          ).map(([valor, rotulo, ajuda]) => (
            <label
              key={valor}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 ${
                decisao === valor ? "border-laranja bg-superficie" : "border-linha"
              }`}
            >
              <input
                type="radio"
                name="decisao"
                checked={decisao === valor}
                onChange={() => setDecisao(valor)}
                className="mt-1"
              />
              <span>
                <span className="text-[13.5px] font-semibold">{rotulo}</span>
                <span className="mt-0.5 block text-[12.5px] text-tinta-media">{ajuda}</span>
              </span>
            </label>
          ))}
        </div>
        {!um && decisao === "em_tratativa" && jaEmTratativa > 0 && (
          <p className="mt-2 text-[12px] text-tinta-fraca">
            {alvo.length === 0
              ? "Todas as selecionadas já estão em tratativa: não há o que assumir."
              : `${contar(jaEmTratativa, "já está em tratativa e fica como está", "já estão em tratativa e ficam como estão")}.`}
          </p>
        )}
      </fieldset>

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">
          Observação{precisaObservacao && <span className="text-laranja-tinta"> *</span>}
        </span>
        <textarea
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          rows={3}
          placeholder="O que foi verificado e o que foi decidido."
          className="mt-1 w-full resize-y rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
        />
        {precisaObservacao && (
          <span className="mt-1 block text-[11.5px] text-tinta-fraca">
            Fica no Histórico de Movimentações. É o que explica a decisão a quem consultar depois.
          </span>
        )}
      </label>

      {tratar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(tratar.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onFechar}
          className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold hover:bg-superficie-2"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={!podeEnviar}
          onClick={() => tratar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
        >
          {tratar.isPending
            ? "Registrando…"
            : `${decisao === "resolvida" ? "Resolver" : "Assumir"}${um ? "" : ` ${contar(pendencias, "pendência", "pendências")}`}`}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Confirmar, de uma vez, a sugestão que o motor deu para cada lançamento.
 *
 * ⚠️ É a sugestão COMO ESTÁ: não escolhe linha, não aplica aos iguais e não cria
 * regra. Isso continua no "Confirmar ou trocar de linha" do cartão. A rota
 * confirma uma a uma, com um evento no Histórico para cada — é por evento que
 * o admin desfaz uma confirmação (035), e um evento para o lote não deixaria
 * desfazer só uma.
 */
function ModalConfirmarSugestoes({
  itens,
  onFechar,
  onConcluido,
  onParcial,
}: {
  itens: Excecao[];
  onFechar: () => void;
  onConcluido: (mensagem: string) => void;
  /** Um lote falhou depois de outros terem gravado: a tela precisa se refazer. */
  onParcial: () => void;
}) {
  const ids = [...new Set(itens.map((e) => e.entidade_id).filter((x): x is string => !!x))];
  const lotes = Math.ceil(ids.length / LIMITE_CONFIRMAR);
  const [feitos, setFeitos] = useState(0);
  // Cada lote é uma transação no servidor: se o terceiro falha, os dois
  // primeiros ficaram gravados — e a mensagem de erro precisa dizer isso.
  const parcial = useRef({ informadas: 0, confirmadas: 0 });

  const confirmar = useMutation({
    mutationFn: async () => {
      parcial.current = { informadas: 0, confirmadas: 0 };
      setFeitos(0);
      for (let i = 0; i < ids.length; i += LIMITE_CONFIRMAR) {
        const r = await api.post<{ informadas: number; confirmadas: number }>(
          "/api/alocacoes/confirmar-em-lote",
          { lancamento_ids: ids.slice(i, i + LIMITE_CONFIRMAR) },
        );
        parcial.current = {
          informadas: parcial.current.informadas + r.informadas,
          confirmadas: parcial.current.confirmadas + r.confirmadas,
        };
        setFeitos(Math.min(i + LIMITE_CONFIRMAR, ids.length));
      }
      return parcial.current;
    },
    onSuccess: ({ informadas, confirmadas }) => {
      const sobra = informadas - confirmadas;
      const base =
        confirmadas === 0
          ? "Nenhuma sugestão foi confirmada."
          : `${contar(confirmadas, "sugestão confirmada", "sugestões confirmadas")}, cada uma com o seu registro no Histórico. ${
              confirmadas === 1 ? "A pendência saiu" : "As pendências saíram"
            } da fila.`;
      const resto =
        sobra === 1
          ? " 1 lançamento já não tinha sugestão em aberto — provavelmente alguém decidiu antes — e ficou como estava."
          : sobra > 1
            ? ` ${sobra.toLocaleString("pt-BR")} lançamentos já não tinham sugestão em aberto — provavelmente alguém decidiu antes — e ficaram como estavam.`
            : "";
      onConcluido(base + resto);
    },
    onError: () => {
      if (parcial.current.confirmadas > 0) onParcial();
    },
  });

  return (
    <Modal titulo={`Confirmar ${contar(ids.length, "sugestão", "sugestões")}`} onFechar={onFechar}>
      <p className="text-[13px] text-tinta-media">
        Para cada lançamento, confirma a linha e a subcategoria que o motor sugeriu, do jeito que ele
        sugeriu. É uma confirmação por lançamento, cada uma com o seu registro no Histórico — por isso
        um admin consegue desfazer uma só, se precisar. A pendência “Sugestão a confirmar” de cada um
        sai da fila no mesmo passo.
      </p>
      <p className="mt-2 text-[13px] text-tinta-media">
        Não aplica aos iguais nem cria regra para os próximos meses. Se em algum a sugestão não for a
        certa, tire-o da seleção e use “Confirmar ou trocar de linha” no cartão dele para escolher outra
        linha.
      </p>

      <ul className="thin-scroll mt-3 grid max-h-56 gap-1 overflow-y-auto rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px]">
        {itens.map((e) => (
          <li key={e.excecao_id} className="flex justify-between gap-4">
            <span className="truncate font-medium">{String(e.contexto?.lancamento ?? "Lançamento sem descrição")}</span>
            <span className="num shrink-0 text-tinta-fraca">
              {[e.contexto?.fornecedor, e.contexto?.competencia].filter((x) => x != null).map(String).join(" · ")}
            </span>
          </li>
        ))}
      </ul>

      {lotes > 1 && (
        <p className="mt-2 text-[12px] text-tinta-fraca">
          São mais de {LIMITE_CONFIRMAR.toLocaleString("pt-BR")}: vão em {lotes} lotes, um depois do outro. Se
          um falhar, os anteriores ficam gravados.
        </p>
      )}

      {confirmar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(confirmar.error as FalhaApi).message}
          {parcial.current.confirmadas > 0 &&
            ` Antes da falha, ${contar(parcial.current.confirmadas, "sugestão foi confirmada", "sugestões foram confirmadas")} e ficaram gravadas; o lote que falhou não gravou nada. Tentar de novo confirma só o que faltou.`}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        {confirmar.isPending && lotes > 1 && (
          <span className="num mr-auto text-[12px] text-tinta-fraca">
            {feitos.toLocaleString("pt-BR")} de {ids.length.toLocaleString("pt-BR")}
          </span>
        )}
        <button
          type="button"
          onClick={onFechar}
          disabled={confirmar.isPending}
          className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold hover:bg-superficie-2 disabled:cursor-not-allowed disabled:opacity-45"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={confirmar.isPending || ids.length === 0}
          onClick={() => confirmar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
        >
          {confirmar.isPending ? "Confirmando…" : `Confirmar ${contar(ids.length, "sugestão", "sugestões")}`}
        </button>
      </div>
    </Modal>
  );
}
